import { useState, useEffect, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'

interface HealthMetrics {
  totalParents: number
  totalChildren: number
  totalSessions: number
  totalPracticeTimeMs: number
  parentsLast24h: number
  childrenLast24h: number
  sessionsLast24h: number
  sessionsLast7d: number
}

interface AuditEvent {
  id: number
  actorId: number
  actorType: string
  action: string
  targetType: string | null
  targetId: number | null
  detail: string | null
  createdAt: number
}

interface FeatureFlag {
  id: number
  name: string
  enabled: boolean
  description: string | null
  createdAt: number
  updatedAt: number
}

interface FamilyUser {
  familyId: number
  familyName: string
  createdAt: number
  members: {
    parentId: number
    email: string
    firstName: string | null
    lastName: string | null
    role: string
    isVerified: boolean
    createdAt: number
  }[]
  children: {
    childId: number
    username: string
    displayName: string
    birthDate: number | null
    createdAt: number
  }[]
}


function formatDuration(ms: number) {
  const hours = Math.floor(ms / 3_600_000)
  const minutes = Math.floor((ms % 3_600_000) / 60_000)
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

function formatTime(epoch: number) {
  return new Date(epoch).toLocaleString()
}

function MetricCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  )
}

export default function AdminPage() {
  const [health, setHealth] = useState<HealthMetrics | null>(null)
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [eventTotal, setEventTotal] = useState(0)
  const [eventPage, setEventPage] = useState(0)
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [newFlagName, setNewFlagName] = useState('')
  const [newFlagDesc, setNewFlagDesc] = useState('')
  const [families, setFamilies] = useState<FamilyUser[]>([])

  const [error, setError] = useState('')

  useEffect(() => {
    apiFetch<HealthMetrics>('/admin/health').then(setHealth).catch(() => setError('Failed to load health metrics'))
    apiFetch<FamilyUser[]>('/admin/users').then(setFamilies).catch(() => {})
    loadFlags()
  }, [])

  useEffect(() => {
    loadEvents(eventPage)
  }, [eventPage])

  async function loadEvents(page: number) {
    try {
      const data = await apiFetch<{ events: AuditEvent[]; total: number }>(`/admin/activity?page=${page}&size=20`)
      setEvents(data.events)
      setEventTotal(data.total)
    } catch {
      setError('Failed to load activity')
    }
  }

  async function loadFlags() {
    try {
      const data = await apiFetch<FeatureFlag[]>('/admin/flags')
      setFlags(data)
    } catch {
      setError('Failed to load flags')
    }
  }

  async function toggleFlag(id: number, enabled: boolean) {
    await apiFetch(`/admin/flags/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    })
    loadFlags()
    loadEvents(eventPage)
  }

  async function createFlag(e: FormEvent) {
    e.preventDefault()
    if (!newFlagName.trim()) return
    await apiFetch('/admin/flags', {
      method: 'POST',
      body: JSON.stringify({ name: newFlagName.trim(), description: newFlagDesc.trim() || null, enabled: false }),
    })
    setNewFlagName('')
    setNewFlagDesc('')
    loadFlags()
    loadEvents(eventPage)
  }

  async function deleteFlag(id: number) {
    await apiFetch(`/admin/flags/${id}`, { method: 'DELETE' })
    loadFlags()
    loadEvents(eventPage)
  }


  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      <h1 className="text-3xl font-black text-primary">Admin</h1>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {health && (
        <section className="space-y-3">
          <h2 className="text-lg font-bold">Health</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Parents" value={health.totalParents} />
            <MetricCard label="Children" value={health.totalChildren} />
            <MetricCard label="Sessions" value={health.totalSessions} />
            <MetricCard label="Practice time" value={formatDuration(health.totalPracticeTimeMs)} />
            <MetricCard label="New parents (24h)" value={health.parentsLast24h} />
            <MetricCard label="New children (24h)" value={health.childrenLast24h} />
            <MetricCard label="Sessions (24h)" value={health.sessionsLast24h} />
            <MetricCard label="Sessions (7d)" value={health.sessionsLast7d} />
          </div>
        </section>
      )}

      <section>
        <Link
          to="/admin/magnum-workshop"
          className="block bg-card border-2 border-primary/20 hover:border-primary/40 rounded-lg p-4 transition"
        >
          <h2 className="text-lg font-bold text-primary">Tim Workshop</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Context tester, lines, easter eggs, speak rules, and personality studio
          </p>
        </Link>
      </section>

      {families.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-bold">Families & Users</h2>
          <div className="space-y-3">
            {families.map((family) => (
              <div key={family.familyId} className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between">
                  <div>
                    <span className="font-semibold">{family.familyName}</span>
                    <span className="text-xs text-muted-foreground ml-2">ID: {family.familyId}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{formatTime(family.createdAt)}</span>
                </div>
                <div className="divide-y divide-border">
                  {family.members.map((member) => (
                    <div key={member.parentId} className="px-4 py-2 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          member.role === 'ADMIN'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {member.role}
                        </span>
                        <span className="font-medium">
                          {member.firstName || ''} {member.lastName || ''}
                        </span>
                        <span className="text-muted-foreground">{member.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {!member.isVerified && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Unverified</span>
                        )}
                        <span className="text-xs text-muted-foreground">Parent #{member.parentId}</span>
                      </div>
                    </div>
                  ))}
                  {family.children.map((child) => (
                    <div key={child.childId} className="px-4 py-2 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-green-100 text-green-700">CHILD</span>
                        <span className="font-medium">{child.displayName}</span>
                        <span className="text-muted-foreground">@{child.username}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">Child #{child.childId}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-bold">Feature Flags</h2>
        <div className="space-y-2">
          {flags.map((flag) => (
            <div key={flag.id} className="flex items-center justify-between bg-card border border-border rounded-lg p-3">
              <div>
                <span className="font-medium">{flag.name}</span>
                {flag.description && (
                  <span className="text-sm text-muted-foreground ml-2">{flag.description}</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => toggleFlag(flag.id, !flag.enabled)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition ${
                    flag.enabled
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {flag.enabled ? 'ON' : 'OFF'}
                </button>
                <button
                  onClick={() => deleteFlag(flag.id)}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={createFlag} className="flex gap-2">
          <input
            type="text"
            placeholder="Flag name"
            value={newFlagName}
            onChange={(e) => setNewFlagName(e.target.value)}
            className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-sm"
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newFlagDesc}
            onChange={(e) => setNewFlagDesc(e.target.value)}
            className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-sm"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition"
          >
            Add
          </button>
        </form>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-bold">Activity Feed</h2>
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground">No events yet</p>
        ) : (
          <div className="space-y-1">
            {events.map((event) => (
              <div key={event.id} className="flex items-center justify-between bg-card border border-border rounded-lg px-3 py-2 text-sm">
                <div>
                  <span className="font-medium">{event.action}</span>
                  {event.targetType && (
                    <span className="text-muted-foreground"> on {event.targetType} #{event.targetId}</span>
                  )}
                  {event.detail && (
                    <span className="text-muted-foreground"> — {event.detail}</span>
                  )}
                </div>
                <span className="text-muted-foreground text-xs whitespace-nowrap ml-4">
                  {formatTime(event.createdAt)}
                </span>
              </div>
            ))}
          </div>
        )}
        {eventTotal > 20 && (
          <div className="flex gap-2 justify-center">
            <button
              disabled={eventPage === 0}
              onClick={() => setEventPage(eventPage - 1)}
              className="px-3 py-1 text-sm border rounded disabled:opacity-30"
            >
              Prev
            </button>
            <span className="text-sm text-muted-foreground py-1">
              Page {eventPage + 1} of {Math.ceil(eventTotal / 20)}
            </span>
            <button
              disabled={(eventPage + 1) * 20 >= eventTotal}
              onClick={() => setEventPage(eventPage + 1)}
              className="px-3 py-1 text-sm border rounded disabled:opacity-30"
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  )
}
